/* Two-step pack builder: pick a pack size, then a color per mug slot.
   Adds the chosen MUG variants (one per slot) to the cart so Shopify stock
   decreases per colour; the Duo/Trio price comes from automatic discounts
   "Pack Duo" / "Pack Trio" in Shopify Admin. */

class MugPackBuilder extends HTMLElement {
  connectedCallback() {
    if (this.dataset.bound) return;
    this.dataset.bound = 'true';

    this.packs = JSON.parse(this.querySelector('[data-mpb-packs]').textContent);
    this.colors = JSON.parse(this.querySelector('[data-mpb-colors]').textContent);
    this.isEnglish = this.dataset.mpbLang === 'en';

    this.packCards = Array.from(this.querySelectorAll('[data-mpb-pack-card]'));
    this.colorsStep = this.querySelector('[data-mpb-colors-step]');
    this.slotsContainer = this.querySelector('[data-mpb-slots]');
    this.missingEl = this.querySelector('[data-mpb-missing]');
    this.summaryEl = this.querySelector('[data-mpb-summary]');
    this.errorEl = this.querySelector('[data-mpb-error]');
    this.submitButton = this.querySelector('[data-mpb-submit]');

    this.selectedPackIndex = null;
    this.selectedColors = [];
    this.isSubmitting = false;



    this.packCards.forEach((card, index) => {
      card.addEventListener('click', () => this.selectPack(index));
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const nextIndex = event.key === 'ArrowRight'
          ? (index + 1) % this.packCards.length
          : (index - 1 + this.packCards.length) % this.packCards.length;
        this.packCards[nextIndex].focus();
        this.selectPack(nextIndex);
      });
    });

    this.submitButton.addEventListener('click', () => this.submit());
  }

  selectPack(index) {
    if (this.isSubmitting || !this.packs[index] || this.packs[index].available === false) return;
    this.selectedPackIndex = index;
    this.packCards.forEach((card, i) => {
      card.setAttribute('aria-checked', i === index ? 'true' : 'false');
    });

    const pack = this.packs[index];
    this.selectedColors = new Array(pack.mugCount).fill(null);
    this.renderSlots(pack.mugCount);
    this.colorsStep.hidden = false;

    // Pre-fill every mug with the colour picked above (or the first in stock): Duo/Trio in 2 taps, still editable per mug.
    const picked = this.closest('.shopify-section')?.querySelector('variant-picker input:checked')?.value;
    const color = this.colors.find((c) => c.available && c.name === picked) || this.colors.find((c) => c.available);
    if (color) {
      this.slotsContainer.querySelectorAll('.mug-pack-builder__slot').forEach((slot, slotIndex) => {
        this.selectColor(slotIndex, color, slot.querySelectorAll('.mug-pack-builder__swatch')[this.colors.indexOf(color)]);
      });
    }

    this.updateState();
  }

  renderSlots(mugCount) {
    this.slotsContainer.innerHTML = '';

    for (let slotIndex = 0; slotIndex < mugCount; slotIndex += 1) {
      const slot = document.createElement('div');
      slot.className = 'mug-pack-builder__slot';

      const label = document.createElement('p');
      label.className = 'mug-pack-builder__slot-label';
      label.id = `mpb-slot-label-${slotIndex}`;
      label.textContent = `${this.isEnglish ? 'Cup' : 'Mug'} ${slotIndex + 1}`;
      slot.appendChild(label);

      const swatchRow = document.createElement('div');
      swatchRow.className = 'mug-pack-builder__swatches';
      swatchRow.setAttribute('role', 'group');
      swatchRow.setAttribute('aria-labelledby', label.id);

      this.colors.forEach((color) => {
        const swatch = document.createElement('button');
        swatch.type = 'button';
        swatch.className = 'mug-pack-builder__swatch';
        swatch.style.background = color.hex;
        swatch.setAttribute('aria-pressed', 'false');
        swatch.title = color.name;

        const visuallyHidden = document.createElement('span');
        visuallyHidden.className = 'visually-hidden';
        visuallyHidden.textContent = color.name;
        swatch.appendChild(visuallyHidden);

        if (!color.available) {
          swatch.disabled = true;
          swatch.setAttribute('aria-label', `${color.name} (${this.isEnglish ? 'out of stock' : 'rupture de stock'})`);
        } else {
          swatch.addEventListener('click', () => this.selectColor(slotIndex, color, swatch));
        }

        swatchRow.appendChild(swatch);
      });

      slot.appendChild(swatchRow);
      const preview = document.createElement('img');
      preview.className = 'mug-pack-builder__preview';
      preview.width = 56;
      preview.height = 56;
      preview.hidden = true;
      slot.appendChild(preview);
      this.slotsContainer.appendChild(slot);
    }
  }

  selectColor(slotIndex, color, swatchEl) {
    if (this.isSubmitting || !color.available) return;
    this.selectedColors[slotIndex] = color.name;

    const slot = swatchEl.closest('.mug-pack-builder__slot');
    slot.querySelectorAll('.mug-pack-builder__swatch').forEach((el) => {
      el.setAttribute('aria-pressed', el === swatchEl ? 'true' : 'false');
    });

    slot.querySelector('.mug-pack-builder__slot-label').textContent = `${this.isEnglish ? 'Cup' : 'Mug'} ${slotIndex + 1} - ${color.name}`;
    const preview = slot.querySelector('.mug-pack-builder__preview');
    if (color.image) {
      preview.src = color.image;
      preview.alt = color.name;
      preview.hidden = false;
    }

    this.showColorInGallery(color);
    this.updateState();
  }

  showColorInGallery(color) {
    if (!color.image) return;
    const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
    const slideshow = gallery?.querySelector('slideshow-component');
    if (!slideshow?.slides || typeof slideshow.select !== 'function') return;

    // Select the existing photo: keep its responsive sources, zoom and controls.
    // Shopify's preview proxy changes the host and image widths, not the path.
    const imagePath = new URL(color.image, window.location.href).pathname;
    const index = slideshow.slides.findIndex((slide) => {
      const image = slide.querySelector('img');
      return image && new URL(image.src, window.location.href).pathname === imagePath;
    });
    if (index !== -1) slideshow.select(index, undefined, { animate: false });
  }

  updateState() {
    if (this.isSubmitting) {
      this.submitButton.disabled = true;
      return;
    }
    this.errorEl.hidden = true;

    const missingIndex = this.selectedColors.findIndex((color) => color === null);

    if (missingIndex !== -1) {
      this.missingEl.textContent = this.isEnglish
        ? `Choose a color for cup ${missingIndex + 1}`
        : `Choisissez une couleur pour Mug ${missingIndex + 1}`;
      this.summaryEl.textContent = '';
      this.submitButton.disabled = true;
      return;
    }

    this.missingEl.textContent = '';
    const pack = this.packs[this.selectedPackIndex];
    this.summaryEl.textContent = `${pack.label} - ${this.selectedColors.join(', ')} - ${pack.priceFormatted}`;
    this.submitButton.disabled = false;
  }

  async submit() {
    if (this.isSubmitting || this.selectedPackIndex === null || this.submitButton.disabled) return;
    this.isSubmitting = true;
    this.setAttribute('aria-busy', 'true');

    const counts = new Map();
    this.selectedColors.forEach((name) => {
      const id = this.colors.find((color) => color.name === name)?.id;
      counts.set(id, (counts.get(id) || 0) + 1);
    });

    this.submitButton.disabled = true;
    this.submitButton.textContent = this.isEnglish ? 'Adding…' : 'Ajout en cours…';

    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [...counts].map(([id, quantity]) => ({ id, quantity })),
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || (this.isEnglish ? 'Could not add this pack to the cart.' : 'Impossible d’ajouter ce pack au panier.'));
      }

      window.location.href = `${root}cart`;
    } catch (error) {
      this.isSubmitting = false;
      this.removeAttribute('aria-busy');
      this.errorEl.textContent = error.message;
      this.errorEl.hidden = false;
      this.submitButton.disabled = false;
      this.submitButton.textContent = this.isEnglish ? 'Add to cart' : 'Ajouter au panier';
    }
  }
}

customElements.define('mug-pack-builder', MugPackBuilder);
